import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Alert, Box, Typography, Stack, Link, Tooltip, Collapse } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AppointmentTypeChanges } from '../../types/appointmentChanges';
import { AppointmentType, ExpandableFieldItem } from '@features/appointment-types';
import { UserTemplateItem } from '../../../../types/types';
import UpdatePreviewPopover from './UpdatePreviewPopover';
import CreateNewPreviewPopover from './CreateNewPreviewPopover';
import { useContentDimensions } from '../../../../hooks/useContentDimensions';
import { PopoverContentWrapper } from './PopoverContent';
import { UpdatePreviewContent } from './UpdatePreviewContent';
import { CreateNewPreviewContent } from './CreateNewPreviewContent';

// Animation constants
const COLLAPSE_DURATION = 300; // ms
const MARGIN_DURATION = 50; // ms
const MARGIN_SPACING = 2; // theme spacing units

export interface DirtyChangesAlertProps {
  show: boolean;
  appointmentTypeName: string;
  changes?: AppointmentTypeChanges;
  selectedAppointmentType?: AppointmentType;
  currentDuration?: number;
  currentTelehealth?: boolean;
  currentForms?: ExpandableFieldItem[];
  currentReminders?: ExpandableFieldItem[];
  currentTemplate?: UserTemplateItem | null;
  onUpdateAppointmentType?: () => void | Promise<void>;
  onCreateNew?: () => void;
}

const hasEnoughViewportSpace = (element: HTMLElement, contentHeight: number) => {
  const rect = element.getBoundingClientRect();

  // Return true has enough space
  return rect.top >= contentHeight - 32;
};

export const DirtyChangesAlert: React.FC<DirtyChangesAlertProps> = ({
  show,
  appointmentTypeName,
  changes,
  selectedAppointmentType,
  currentDuration,
  currentTelehealth,
  currentForms,
  currentReminders,
  currentTemplate,
  onUpdateAppointmentType,
  onCreateNew,
}) => {
  const [updateAnchorEl, setUpdateAnchorEl] = useState<HTMLElement | null>(null);
  const [createAnchorEl, setCreateAnchorEl] = useState<HTMLElement | null>(null);
  const { measureContent } = useContentDimensions();

  // Cache for popover dimensions
  const [cachedDimensions, setCachedDimensions] = useState<{
    update: { width: number; height: number } | null;
    create: { width: number; height: number } | null;
  }>({ update: null, create: null });

  // Pre-calculate dimensions when content changes
  useEffect(() => {
    const calculateDimensions = async () => {
      // Only calculate if the alert is shown
      if (!show) return;

      // Measure update preview content
      const updateDimensions = await measureContent(() => (
        <PopoverContentWrapper>
          <UpdatePreviewContent
            appointmentTypeName={appointmentTypeName}
            changes={changes}
            selectedAppointmentType={selectedAppointmentType}
            currentDuration={currentDuration}
            currentTelehealth={currentTelehealth}
            currentForms={currentForms}
            currentReminders={currentReminders}
            currentTemplate={currentTemplate}
          />
        </PopoverContentWrapper>
      ));

      // Measure create preview content
      const createDimensions = await measureContent(() => (
        <PopoverContentWrapper>
          <CreateNewPreviewContent
            appointmentTypeName={appointmentTypeName}
            changes={changes}
            currentDuration={currentDuration}
            currentTelehealth={currentTelehealth}
            currentForms={currentForms}
            currentReminders={currentReminders}
            currentTemplate={currentTemplate}
          />
        </PopoverContentWrapper>
      ));

      setCachedDimensions({
        update: updateDimensions,
        create: createDimensions,
      });
    };

    calculateDimensions();
  }, [
    show,
    measureContent,
    appointmentTypeName,
    changes,
    selectedAppointmentType,
    currentDuration,
    currentTelehealth,
    currentForms,
    currentReminders,
    currentTemplate,
  ]);

  const handleUpdateMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const target = e.currentTarget;

      // Use cached dimensions if available
      const dimensions = cachedDimensions.update;
      if (!dimensions) {
        // If dimensions aren't cached yet, don't show popover
        return;
      }

      if (!hasEnoughViewportSpace(target, dimensions.height)) {
        return;
      }

      setUpdateAnchorEl(target);
    },
    [cachedDimensions.update],
  );

  const handleUpdateMouseLeave = useCallback(() => {
    setUpdateAnchorEl(null);
  }, []);

  const handleCreateMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const target = e.currentTarget;

      // Use cached dimensions if available
      const dimensions = cachedDimensions.create;
      if (!dimensions) {
        return;
      }

      if (!hasEnoughViewportSpace(target, dimensions.height)) {
        return;
      }

      setCreateAnchorEl(target);
    },
    [cachedDimensions.create],
  );

  const handleCreateMouseLeave = useCallback(() => {
    setCreateAnchorEl(null);
  }, []);

  return (
    <>
      <Box
        sx={{
          mb: show ? MARGIN_SPACING : 0,
          transition: `margin-bottom ${MARGIN_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          transitionDelay: show ? '0ms' : `${COLLAPSE_DURATION - 100}ms`, // margin animates first when opening, last when closing
        }}
      >
        <Collapse in={show} timeout={COLLAPSE_DURATION}>
          <Alert
            severity="info"
            sx={{
              py: 1,
              '& .MuiAlert-message': {
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              },
              '& .MuiAlert-icon': {
                alignItems: 'center',
              },
            }}
            icon={<InfoOutlinedIcon />}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Tooltip
                title="You have made changes that differ from the appointment type defaults. You can update the existing appointment type or create a new one."
                placement="top"
              >
                <Stack>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    Differences detected from your <strong>{appointmentTypeName}</strong>{' '}
                    appointment type
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Want to make these changes your default?
                  </Typography>
                </Stack>
              </Tooltip>
              <Stack direction="row" spacing={2} alignItems="center">
                {onUpdateAppointmentType && (
                  <Link
                    component="button"
                    variant="caption"
                    onClick={onUpdateAppointmentType}
                    onMouseEnter={handleUpdateMouseEnter}
                    onMouseLeave={handleUpdateMouseLeave}
                    sx={{ cursor: 'pointer', fontWeight: 500 }}
                  >
                    Update this appointment type
                  </Link>
                )}
                {onCreateNew && (
                  <Link
                    component="button"
                    variant="caption"
                    onClick={onCreateNew}
                    onMouseEnter={handleCreateMouseEnter}
                    onMouseLeave={handleCreateMouseLeave}
                    sx={{ cursor: 'pointer', fontWeight: 500 }}
                  >
                    Create new appointment type
                  </Link>
                )}
              </Stack>
            </Box>
          </Alert>
        </Collapse>
      </Box>

      {/* Popovers */}
      <UpdatePreviewPopover
        open={Boolean(updateAnchorEl)}
        anchorEl={updateAnchorEl}
        appointmentTypeName={appointmentTypeName}
        changes={changes}
        selectedAppointmentType={selectedAppointmentType}
        currentDuration={currentDuration}
        currentTelehealth={currentTelehealth}
        currentForms={currentForms}
        currentReminders={currentReminders}
        currentTemplate={currentTemplate}
        onClose={() => setUpdateAnchorEl(null)}
      />
      <CreateNewPreviewPopover
        open={Boolean(createAnchorEl)}
        anchorEl={createAnchorEl}
        appointmentTypeName={appointmentTypeName}
        changes={changes}
        currentDuration={currentDuration}
        currentTelehealth={currentTelehealth}
        currentForms={currentForms}
        currentReminders={currentReminders}
        currentTemplate={currentTemplate}
        onClose={() => setCreateAnchorEl(null)}
      />
    </>
  );
};

export default DirtyChangesAlert;
